import { useState } from 'react'
import { ChevronRightIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'
import { runPageHref } from '../../../lib/component-links'
import { formatRelativeTime } from '../../../lib/time'
import { RunStatusPill } from './RunStatusPill'
import { DetailLoading } from './DetailStates'
import { ConfirmDialog } from './DeleteConfirmDialog'

export interface RunsLinkTableRow {
  id: string
  state: string
  createdAt: string
  followUpActive?: boolean
}

interface RunsLinkTableProps {
  rows: RunsLinkTableRow[]
  type: 'custom'
  isLoading: boolean
  emptyMessage?: string
  /**
   * Optional per-row cancel handler. When provided, cancellable rows
   * (state ∈ {pending_dispatch, running}) get an inline cancel button
   * gated by a confirm dialog. Errors are the caller's responsibility.
   */
  onCancel?: (runId: string) => Promise<void>
  /** Human-readable agent label for the confirm dialog title. */
  agentLabel?: string
}

const CANCELLABLE = new Set(['pending_dispatch', 'running'])

/**
 * Thin one-row-per-run table. Each row is a single Link that navigates
 * to `/runs?type=<type>&run=<id>` — the dedicated per-run page handles
 * the detailed rendering.
 */
export function RunsLinkTable({
  rows,
  type,
  isLoading,
  emptyMessage = 'No runs yet.',
  onCancel,
  agentLabel
}: RunsLinkTableProps) {
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null)
  const [cancelBusy, setCancelBusy] = useState(false)

  if (isLoading) {
    return <DetailLoading message="Loading runs…" />
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-card px-4 py-6 text-center text-sm text-ink-3 shadow-sm">
        {emptyMessage}
      </div>
    )
  }

  const dialogLabel = agentLabel ?? type.toUpperCase()

  const handleConfirm = async () => {
    if (!onCancel || !cancelTargetId) return
    setCancelBusy(true)
    try {
      await onCancel(cancelTargetId)
    } finally {
      setCancelBusy(false)
      setCancelTargetId(null)
    }
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-line bg-card shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line bg-bg-elev/50">
              <th className="w-32 px-3 py-1.5 text-left text-10 font-medium uppercase tracking-[0.06em] text-ink-4">
                Status
              </th>
              <th className="px-3 py-1.5 text-left text-10 font-medium uppercase tracking-[0.06em] text-ink-4">
                Created
              </th>
              {onCancel ? <th className="w-24" /> : null}
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const cancellable = onCancel && CANCELLABLE.has(row.state)
              return (
                <tr
                  key={row.id}
                  className={cn(
                    'group/run transition-colors hover:bg-accent/30',
                    i > 0 && 'border-t border-line/60'
                  )}
                >
                  <td className="p-0">
                    <Link
                      to={runPageHref(type, row.id)}
                      className="flex h-full items-center px-3 py-1.5"
                    >
                      <span className="flex items-center gap-2">
                        <RunStatusPill state={row.state} />
                        {row.followUpActive ? (
                          <span className="text-11 text-ink-3">findings in follow-up</span>
                        ) : null}
                      </span>
                    </Link>
                  </td>
                  <td className="p-0">
                    <Link
                      to={runPageHref(type, row.id)}
                      className="block px-3 py-1.5 text-xs text-ink-3"
                    >
                      {formatRelativeTime(row.createdAt)}
                    </Link>
                  </td>
                  {onCancel ? (
                    <td className="p-0">
                      {cancellable ? (
                        <div className="flex items-center justify-end px-2 py-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setCancelTargetId(row.id)}
                          >
                            Cancel run
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  ) : null}
                  <td className="p-0">
                    <Link
                      to={runPageHref(type, row.id)}
                      aria-label="Open run"
                      className="flex h-full items-center justify-end px-3 py-1.5 text-ink-4 group-hover/run:text-ink-2"
                    >
                      <ChevronRightIcon className="size-4" />
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={cancelTargetId !== null}
        onOpenChange={(open) => {
          if (!open && !cancelBusy) setCancelTargetId(null)
        }}
        title={`Cancel this ${dialogLabel} run?`}
        description="The agent will stop on its next checkpoint and the run will be marked cancelled."
        confirmLabel="Cancel run"
        destructive
        busy={cancelBusy}
        onConfirm={() => void handleConfirm()}
      />
    </>
  )
}
